import { useMemo } from 'react';
import { Keyboard } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Kbd,
} from '@leanopx/design-system';
import { t, type StringKey } from '@/lib/i18n';

/**
 * ShortcutsCheatsheet — the global `?` overlay.
 *
 * A Radix Dialog rendered inside the portal shell (mounted in
 * `AppShell`). The shell binds a window `keydown` listener on `?` and
 * toggles `open` here; the dialog handles Esc + focus-trap + click-out
 * internally via Radix.
 *
 * Content is a 2-column grid of category cards (Navigation, Inbox,
 * Drafts, Editing). All copy comes from `STRINGS['shortcuts.*']` in
 * `apps/web/src/lib/i18n.ts` — never hardcode here.
 *
 * Theme tokens only: `--color-canvas`, `--color-line`, `--color-surface`,
 * `--color-accent`, `--color-ink-*`. Layout uses logical CSS (`ps-`/`pe-`,
 * `border-block-start`/`-end`).
 *
 * The cheatsheet itself is _content-only_: the shortcuts shown describe
 * keybindings other surfaces wire up, not what this component does. This
 * keeps the component a pure renderer driven by STRINGS — adding a new
 * shortcut row requires adding a STRINGS pair + a row in `CATEGORIES`.
 */

interface ShortcutRow {
  keysKey: StringKey;
  labelKey: StringKey;
}

interface ShortcutCategory {
  titleKey: StringKey;
  rows: ShortcutRow[];
}

const CATEGORIES: ShortcutCategory[] = [
  {
    titleKey: 'shortcuts.category.navigation',
    rows: [
      { keysKey: 'shortcuts.nav.briefing.keys', labelKey: 'shortcuts.nav.briefing.label' },
      { keysKey: 'shortcuts.nav.ask.keys', labelKey: 'shortcuts.nav.ask.label' },
      { keysKey: 'shortcuts.nav.dossiers.keys', labelKey: 'shortcuts.nav.dossiers.label' },
      { keysKey: 'shortcuts.nav.scorecard.keys', labelKey: 'shortcuts.nav.scorecard.label' },
      { keysKey: 'shortcuts.nav.rocks.keys', labelKey: 'shortcuts.nav.rocks.label' },
      { keysKey: 'shortcuts.nav.issues.keys', labelKey: 'shortcuts.nav.issues.label' },
    ],
  },
  {
    titleKey: 'shortcuts.category.inbox',
    rows: [
      { keysKey: 'shortcuts.inbox.unread.keys', labelKey: 'shortcuts.inbox.unread.label' },
      { keysKey: 'shortcuts.inbox.archive.keys', labelKey: 'shortcuts.inbox.archive.label' },
      { keysKey: 'shortcuts.inbox.cheatsheet.keys', labelKey: 'shortcuts.inbox.cheatsheet.label' },
      { keysKey: 'shortcuts.inbox.esc.keys', labelKey: 'shortcuts.inbox.esc.label' },
    ],
  },
  {
    titleKey: 'shortcuts.category.drafts',
    rows: [
      { keysKey: 'shortcuts.drafts.approve.keys', labelKey: 'shortcuts.drafts.approve.label' },
      { keysKey: 'shortcuts.drafts.edit.keys', labelKey: 'shortcuts.drafts.edit.label' },
      { keysKey: 'shortcuts.drafts.reject.keys', labelKey: 'shortcuts.drafts.reject.label' },
    ],
  },
  {
    titleKey: 'shortcuts.category.editing',
    rows: [
      { keysKey: 'shortcuts.editing.bold.keys', labelKey: 'shortcuts.editing.bold.label' },
      { keysKey: 'shortcuts.editing.italic.keys', labelKey: 'shortcuts.editing.italic.label' },
      { keysKey: 'shortcuts.editing.link.keys', labelKey: 'shortcuts.editing.link.label' },
      { keysKey: 'shortcuts.editing.undo.keys', labelKey: 'shortcuts.editing.undo.label' },
    ],
  },
];

interface ShortcutsCheatsheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShortcutsCheatsheet({ open, onOpenChange }: ShortcutsCheatsheetProps) {
  // Memoize so the renderer doesn't rebuild the rows array on every
  // re-render of the parent.
  const categories = useMemo(() => CATEGORIES, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="top-[10vh] max-w-3xl"
        aria-describedby="shortcuts-cheatsheet-eyebrow"
      >
        <header className="flex items-start justify-between gap-4 border-block-end border-[var(--color-line)] px-6 py-5">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="mt-0.5 inline-flex size-7 items-center justify-center rounded-[6px] border border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-accent)]"
            >
              <Keyboard className="size-3.5" aria-hidden />
            </span>
            <div>
              <div
                id="shortcuts-cheatsheet-eyebrow"
                className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--color-accent)]"
              >
                {t('shortcuts.eyebrow')}
              </div>
              <DialogTitle className="mt-1 font-sans text-lg tracking-tight text-[var(--color-ink)]">
                {t('shortcuts.title')}
              </DialogTitle>
              <DialogDescription className="sr-only">{t('shortcuts.title')}</DialogDescription>
            </div>
          </div>
        </header>

        <div className="grid max-h-[60vh] grid-cols-1 gap-x-8 gap-y-6 overflow-y-auto px-6 py-5 sm:grid-cols-2">
          {categories.map((category) => (
            <section key={category.titleKey} aria-labelledby={`cat-${category.titleKey}`}>
              <h3
                id={`cat-${category.titleKey}`}
                className="mb-3 font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--color-accent)]"
              >
                {t(category.titleKey)}
              </h3>
              <dl className="grid gap-2">
                {category.rows.map((row) => (
                  <div
                    key={row.keysKey}
                    className="grid grid-cols-[minmax(108px,auto)_1fr] items-center gap-4 border-block-end border-[var(--color-line)] py-1.5 last:border-block-end-0"
                  >
                    <dt className="flex flex-wrap items-center gap-1">
                      <KeyCombo combo={t(row.keysKey)} />
                    </dt>
                    <dd className="text-sm text-[var(--color-ink-secondary)]">{t(row.labelKey)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <footer className="flex items-center justify-between gap-3 border-block-start border-[var(--color-line)] bg-[var(--color-surface)] px-6 py-3">
          <span className="font-mono text-[11px] text-[var(--color-ink-muted)]">
            {t('shortcuts.footer')}
          </span>
          <Button size="sm" variant="accent" onClick={() => onOpenChange(false)}>
            {t('shortcuts.dismiss')}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

/**
 * KeyCombo — render a "Cmd+Shift+R" string as a row of `<Kbd>` chips
 * with `+` separators. Splits on `+` (Cmd combos) and falls back to
 * splitting on whitespace for "g b" sequence shortcuts.
 */
function KeyCombo({ combo }: { combo: string }) {
  const isCombo = combo.includes('+');
  const parts = isCombo ? combo.split('+').map((p) => p.trim()) : combo.split(/\s+/);
  return (
    <>
      {parts.map((part, i) => (
        <span key={`${part}-${i}`} className="inline-flex items-center gap-1">
          <Kbd>{part}</Kbd>
          {i < parts.length - 1 && (
            <span aria-hidden className="text-[11px] text-[var(--color-ink-muted)]">
              {isCombo ? '+' : 'then'}
            </span>
          )}
        </span>
      ))}
    </>
  );
}
